import { useState } from "react";
import { Button, Input } from "@heroui/react";
import { IconAlertTriangle } from "@tabler/icons-react";
import { useLocale, useTranslations } from "../../../../i18n";
import { Turnstile } from "../../turnstile/turnstile";
import toast from "react-hot-toast";
import { useViewStore } from "../../../store/view-store";
import { useAuthStore } from "../../../store/auth-store";
import { useStudioStore } from "../../../store/studio-store";
import { api, ApiError } from "../../../lib/api";

const MIN_PASSWORD_LENGTH = 8;

function RegisterForm() {
  const t = useTranslations("auth");
  const { login } = useAuthStore();
  const { studio } = useStudioStore();

  const [isLoading, setIsLoading] = useState(false);
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  // Jeton Turnstile (anti-robots) : a usage unique, regenere apres chaque
  // tentative.
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileReset, setTurnstileReset] = useState(0);
  const locale = useLocale();

  // Validation en direct : la regle est visible pendant la saisie, pas
  // seulement apres un echec de soumission.
  const isPasswordTooShort =
    password.length > 0 && password.length < MIN_PASSWORD_LENGTH;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");
    setEmailError("");
    setPasswordError("");

    if (!email || !password) {
      setError(t("fill-email-password"));
      return;
    }

    if (password.length < MIN_PASSWORD_LENGTH) {
      setPasswordError(t("password-too-short"));
      return;
    }

    try {
      setIsLoading(true);

      const session = await api("/api/widget/auth/register", {
        method: "POST",
        body: JSON.stringify({
          name: username || undefined,
          email,
          password,
          studioId: studio?.id, // ← tenant scoping
          turnstileToken,
        }),
      });

      // Store auth session in widget store
      login({
        user: {
          id: session.user.id,
          name: session.user.name ?? (username || null),
          email: session.user.email,
        },
        token: session.token,
      });

      toast.success(t("login-successful"));

      // Resume widget navigation
      useViewStore.getState().onLoginSuccess();
    } catch (err) {
      console.error("Register error:", err);
      // Le jeton a ete consomme par cette tentative : on en demande un neuf.
      setTurnstileReset((n) => n + 1);

      if (err instanceof ApiError) {
        // Le serveur renvoie un code stable : on le traduit ici plutot que
        // d'afficher le message brut de l'API.
        switch (err.code) {
          case "PASSWORD_TOO_SHORT":
            setPasswordError(t("password-too-short"));
            return;
          case "TURNSTILE_FAILED":
            setError(t("turnstile-failed"));
            return;
          case "RATE_LIMITED":
            setError(t("too-many-attempts"));
            return;
          case "INVALID_EMAIL":
            setEmailError(t("invalid-email"));
            return;
          // Compte Bookzen existant, mot de passe fourni different. Le refus
          // porte sur le mot de passe, pas sur l'e-mail : l'inscription avec
          // le bon mot de passe rattache maintenant au studio.
          case "EMAIL_EXISTS_WRONG_PASSWORD":
            setPasswordError(t("email-exists-wrong-password"));
            return;
          // Codes de l'ancienne API. Le widget et l'API se deploient
          // separement : pendant la fenetre entre les deux, ces codes peuvent
          // encore arriver.
          case "EMAIL_ALREADY_REGISTERED":
          case "EMAIL_EXISTS_LOGIN_INSTEAD":
            setEmailError(t("email-already-registered"));
            return;
        }

        if (err.status === 409) {
          setEmailError(t("email-already-registered"));
          return;
        }
      }

      setError(t("register-failed"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md mx-auto mt-6">
      {error && (
        <div className="flex items-start gap-2 border-1 border-red-500 rounded-md p-2 mb-4">
          <IconAlertTriangle className="text-red-500" />
          <p className="text-sm">{error}</p>
        </div>
      )}
      <div>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <Input
            type="text"
            label={t("username")}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
          <Input
            type="email"
            label={t("email")}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (emailError) setEmailError("");
            }}
            isInvalid={!!emailError}
            errorMessage={emailError || undefined}
          />
          <Input
            type="password"
            label={t("password")}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (passwordError) setPasswordError("");
            }}
            description={t("password-hint")}
            isInvalid={!!passwordError || isPasswordTooShort}
            errorMessage={
              passwordError ||
              (isPasswordTooShort ? t("password-too-short") : undefined)
            }
          />
          <Turnstile
            onToken={setTurnstileToken}
            resetSignal={turnstileReset}
            language={locale}
          />
          <Button
            size="sm"
            isDisabled={isLoading || !turnstileToken}
            type="submit"
            color="primary"
            className="h-12"
            isLoading={isLoading}
          >
            {t("register")}
          </Button>
        </form>
        <p className="mt-2 text-sm text-gray-500">
          {t("registering-you-accept")}{" "}
          <a
            target="_blank"
            rel="noopener noreferrer"
            href={`#`}
            className="underline cursor-pointer text-sm"
          >
            {t("terms-of-service")}
          </a>
          <a
            target="_blank"
            rel="noopener noreferrer"
            href={`#`}
            className="underline cursor-pointer text-sm"
          >
            {t("privacy-policy")}
          </a>
          .
        </p>

        {/* <div className="flex items-center gap-2 my-4">
          <div className="flex-1 h-px bg-divider" />
          <span className="text-xs text-default-400">{t("or")}</span>
          <div className="flex-1 h-px bg-divider" />
        </div>

        <GoogleSigninBtn /> */}
      </div>
    </div>
  );
}

export default RegisterForm;
